import { OneClick } from '@/components/blocks/OneClick';
import { createCardSX } from '@/components/content/WishListsV2/styles/createCard';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { WISHLIST_NAME_INPUT_MAX_LENGTH } from '@/data/constants/wishlist';
import { useWishLists } from '@/data/Content/WishLists';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { mergeSx } from '@/utils/mergeSx';
import {
	Box,
	Paper,
	SxProps,
	TextField,
	Theme,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { MouseEvent, useCallback, useContext, useEffect, useState } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';
import { useRouter } from 'next/router';

interface WishListCreateCardInterface {
	containerSxProp?: SxProps<Theme>;
	neededTitle?: boolean;
	isFormHiddenProp?: boolean;
	handleCloseCreateWishListProp?: () => void;
	handleCreateWishListProp?: (e: MouseEvent<HTMLButtonElement>) => Promise<void>;
}

const CREATE_LIST_ACTION = 'create-list';

export const WishListsCreateCard: React.FC<WishListCreateCardInterface> = ({
	containerSxProp,
	neededTitle = true,
	isFormHiddenProp = true,
	handleCreateWishListProp,
	handleCloseCreateWishListProp,
}) => {
	const [isFormHidden, setIsFormHidden] = useState<boolean>(isFormHiddenProp);
	const routes = useLocalization('Routes');
	const localization = useLocalization('WishList');
	const { creationData, onNameV2, onCreate, resetCreationData } = useContext(
		ContentContext
	) as ReturnType<typeof useWishLists>;
	const router = useRouter();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const toggle = useCallback(() => {
		setIsFormHidden((prev) => !prev);
		resetCreationData();
	}, [resetCreationData]);

	const handleCreateWishList = async (e: MouseEvent<HTMLButtonElement>) => {
		const response = await onCreate(e);
		const pathname = routes.WishListDetails.route.t();
		const uniqueID = response.wishListCreated?.uniqueID;
		const wishListUrl = `${pathname}?id=${uniqueID}`;
		if (response.wishListCreated?.uniqueID) router.replace(wishListUrl);
		!response?.error && setIsFormHidden(true);
	};

	useEffect(() => {
		if (!router.isReady) return;

		if (router.query.action === CREATE_LIST_ACTION) {
			setIsFormHidden(false);

			const { action, ...restQuery } = router.query;

			router.replace(
				{
					pathname: router.pathname,
					query: restQuery,
				},
				undefined,
				{ shallow: true }
			);
		}
	}, [router, router.isReady, router.query.action]);

	return (
		<Paper
			sx={mergeSx(createCardSX.container, containerSxProp)}
			onClick={() => isFormHidden && toggle()}
		>
			{isFormHidden ? (
				<Box sx={createCardSX.containerIconLabel}>
					<Box sx={createCardSX.iconContainer}>
						<DynamicIcon size={isMobile ? 14 : 24} name={MATERIAL_SYMBOL_ICONS.add} />
					</Box>
					<Typography variant="bodyBaseline" sx={createCardSX.label}>
						{localization.createWishListTitle.t()}
					</Typography>
				</Box>
			) : (
				<Box sx={createCardSX.formContainer}>
					{neededTitle && (
						<Typography variant="bodyBaseline" sx={createCardSX.title}>
							{localization.createWishListTitle.t()}
						</Typography>
					)}

					<TextField
						fullWidth
						size="small"
						data-testid="create-wish-list-name"
						id="create-wish-list-name"
						name="wishListName"
						label={localization.wishListName.t()}
						variant="outlined"
						value={creationData.name}
						inputProps={{ maxLength: WISHLIST_NAME_INPUT_MAX_LENGTH }}
						sx={createCardSX.field}
						onChange={onNameV2}
						error={creationData.error && !!creationData.name}
						helperText={
							creationData.error && !!creationData.name ? localization.InvalidWishListName.t() : ''
						}
					/>
					<OneClick
						data-testid="create-wishlist"
						id="create-wishlist"
						variant="outlined"
						fullWidth
						disabled={!creationData.name || creationData.error}
						sx={createCardSX.saveButton}
						onClick={!!handleCreateWishListProp ? handleCreateWishListProp : handleCreateWishList}
					>
						{localization.createList.t()}
					</OneClick>
					<Box
						sx={createCardSX.containerCancelButton}
						onClick={!!handleCloseCreateWishListProp ? handleCloseCreateWishListProp : toggle}
					>
						<Typography variant="bodyBaseline" sx={createCardSX.cancelButton}>
							{localization.cancel.t()}
						</Typography>
					</Box>
				</Box>
			)}
		</Paper>
	);
};
